import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../legalIdVerification/LegalIdVerification.module.scss";
import { usePageView } from "../../../shared/analytics/usePageView";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../../shared/analytics/documentCaptureEvents";

interface CaptureScreenProps {
  hostRef: RefObject<HTMLDivElement>;
  sdkLoading: boolean;
}

/**
 * CaptureScreen (Regula) — the full-screen host the Regula `<document-reader>` mounts into.
 * Exists as its own component purely so `document_capture_screen` fires reliably on real
 * mount (usePageView's empty-deps effect), mirroring the Veriff variant's CaptureScreen and
 * the SSN Card SDK's own `screens/CaptureScreen.tsx`.
 *
 * MUST be its own keyed, unmountable node: the locator mutates `hostRef.current` imperatively
 * and its teardown clears it via `host.innerHTML = ""`. Leaving "capturing" fully unmounts this
 * node so the teardown hits a detached element instead of wiping the next screen.
 */
export function CaptureScreen({ hostRef, sdkLoading }: CaptureScreenProps) {
  const { t } = useTranslation();
  usePageView(DOCUMENT_CAPTURE_EVENTS.VIEWED.DOCUMENT_CAPTURE_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE });

  return (
    <div className={styles.captureScreen}>
      <h2 className={styles.captureHeading}>{t("legalId.capturing.heading")}</h2>
      <div className={styles.captureBody}>
        <div key="regula-capturing-host" ref={hostRef} className={styles.captureReader} />
        {sdkLoading && (
          <div className={styles.captureLoading} data-testid="regula-capture-loading">
            <div className={styles.spinner} />
          </div>
        )}
      </div>
    </div>
  );
}
